import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LOGOS } from "@/lib/utils";

const TOKEN_KEY = "athlynx_invite_token";

export default function JoinWorkspace() {
  const { tf } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const urlToken = params.get("token") || "";
  const [token, setToken] = useState("");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let t = urlToken;
    if (t) {
      try { localStorage.setItem(TOKEN_KEY, t); } catch { /* private */ }
    } else {
      try { t = localStorage.getItem(TOKEN_KEY) || ""; } catch { /* private */ }
    }
    setToken(t);
    if (!t) { setError("missing"); return; }
    api.get(`/workspaces/invitations/info?token=${encodeURIComponent(t)}`)
      .then((r) => setInfo(r.data))
      .catch(() => setError("notfound"));
  }, [urlToken]);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/workspaces/invitations/accept", { token });
      try { localStorage.removeItem(TOKEN_KEY); } catch { /* private */ }
      toast.success(tf("Invitation acceptée — bienvenue !"));
      window.location.href = "/dashboard";
      return r;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || tf("Acceptation impossible"));
      setBusy(false);
    }
  };

  const decline = () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* private */ }
    navigate(user ? "/dashboard" : "/login");
  };

  const expired = info && info.status !== "pending";
  const emailMismatch = user && info && (user.email || "").toLowerCase() !== (info.email || "").toLowerCase();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="join-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface/80 p-8 space-y-6">
        <img src={LOGOS.white} alt="Athlynx" className="h-7 w-auto dark:block hidden" />
        <img src={LOGOS.black} alt="Athlynx" className="h-7 w-auto dark:hidden block" />

        {!info && !error && (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        )}

        {error && (
          <div className="space-y-4" data-testid="join-error">
            <h1 className="text-xl font-semibold">{tf("Invitation introuvable")}</h1>
            <p className="text-sm text-muted-foreground">
              {tf("Ce lien d'invitation est invalide. Demandez au propriétaire du workspace de vous renvoyer une invitation.")}
            </p>
            <Button asChild variant="secondary"><Link to="/login">{tf("Retour à la connexion")}</Link></Button>
          </div>
        )}

        {info && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-athlynx/10 border border-athlynx/25 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-athlynx" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-semibold truncate" data-testid="join-workspace-name">{info.workspace_name}</h1>
                <p className="text-xs text-muted-foreground">
                  {info.inviter_name ? `${info.inviter_name} ${tf("vous invite à rejoindre ce workspace.")}` : tf("Vous êtes invité à rejoindre ce workspace.")}
                </p>
              </div>
            </div>

            {expired ? (
              <p className="text-sm text-rose-400" data-testid="join-expired">
                {tf("Cette invitation a expiré ou a été révoquée.")}
              </p>
            ) : loading ? (
              <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : !user ? (
              <div className="space-y-3" data-testid="join-auth-required">
                <p className="text-sm text-muted-foreground">
                  {tf("Cette invitation est destinée à")} <span className="font-medium text-foreground">{info.email}</span>.
                </p>
                <div className="flex gap-2">
                  {info.account_exists ? (
                    <Button asChild className="flex-1" data-testid="join-login-btn">
                      <Link to="/login">{tf("Se connecter")}</Link>
                    </Button>
                  ) : (
                    <Button asChild className="flex-1" data-testid="join-register-btn">
                      <Link to="/register">{tf("Créer un compte")}</Link>
                    </Button>
                  )}
                  {info.account_exists ? (
                    <Button asChild variant="secondary" className="flex-1"><Link to="/register">{tf("Créer un compte")}</Link></Button>
                  ) : (
                    <Button asChild variant="secondary" className="flex-1"><Link to="/login">{tf("Se connecter")}</Link></Button>
                  )}
                </div>
              </div>
            ) : emailMismatch ? (
              <div className="space-y-3" data-testid="join-email-mismatch">
                <p className="text-sm text-amber-400">
                  {tf("Cette invitation est destinée à")} {info.email}. {tf("Vous êtes connecté avec")} {user.email}.
                </p>
                <Button variant="secondary" onClick={decline}>{tf("Retour")}</Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button className="flex-1" onClick={accept} disabled={busy} data-testid="join-accept-btn">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {tf("Accepter l'invitation")}
                </Button>
                <Button variant="ghost" onClick={decline} data-testid="join-decline-btn">{tf("Plus tard")}</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
